import { CardType, CARD_DEFINITIONS } from "@/program/Card";
import { getTurnDirectionSVG, getWhileIconSVG } from "./turnIconSVG";
import { PALETTE_MIME_TYPE } from "./CardListEngine";

/** オーブアイコン C-1: 外円 + 内リング + 左上輝き点 */
const ORB_ICON_SVG = `<svg viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-linecap="round"><circle cx="18" cy="18" r="12" stroke-width="2"/><circle cx="18" cy="18" r="6" stroke-width="1" stroke-opacity="0.4"/><circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none"/></svg>`;

/** カードタイプ → SVGアイコン（null は CSS pseudo-element で描画） */
const CARD_ICONS: Partial<Record<CardType, string | null>> = {
  forward: null,
  turn: getTurnDirectionSVG("right"),
  if: null,
  while: getWhileIconSVG(),
  addToOrb: ORB_ICON_SVG,
  call: null,
  action: null,
};

/**
 * カードパレット - 利用可能なカード一覧を表示
 */
export class CardPalette {
  private container: HTMLElement;
  private buttonsWrap: HTMLElement;
  private onCardClickCallback?: (type: CardType) => void;
  private callButton?: HTMLButtonElement;
  private actionButton?: HTMLButtonElement;

  constructor(parentElement: HTMLElement) {
    this.container = document.createElement("div");
    this.container.className = "card-palette";
    this.container.setAttribute("aria-label", "呪文一覧");

    // 「呪文」ラベル
    const label = document.createElement("span");
    label.className = "card-palette__label";
    label.textContent = "呪文";
    this.container.appendChild(label);

    // ボタンラッパー（石板バー）
    this.buttonsWrap = document.createElement("div");
    this.buttonsWrap.className = "card-palette__buttons";
    this.container.appendChild(this.buttonsWrap);

    parentElement.appendChild(this.container);

    this.render();
  }

  /**
   * カードクリック時のコールバックを設定
   */
  onCardClick(callback: (type: CardType) => void): void {
    this.onCardClickCallback = callback;
  }

  /**
   * callカードボタンを表示
   */
  showCallCard(): void {
    if (this.callButton) {
      this.callButton.style.display = "";
    }
  }

  /**
   * callカードボタンを非表示
   */
  hideCallCard(): void {
    if (this.callButton) {
      this.callButton.style.display = "none";
    }
  }

  /**
   * actionカードボタンを表示
   */
  showActionCard(): void {
    if (this.actionButton) {
      this.actionButton.style.display = "";
    }
  }

  /**
   * actionカードボタンを非表示
   */
  hideActionCard(): void {
    if (this.actionButton) {
      this.actionButton.style.display = "none";
    }
  }

  /**
   * UIを描画
   */
  private render(): void {
    const cardTypes: CardType[] = ["forward", "turn", "if", "while"];

    cardTypes.forEach((type) => {
      const definition = CARD_DEFINITIONS[type];
      const button = this.createCardButton(type, definition.label);
      this.buttonsWrap.appendChild(button);
    });

    // 「オーブ」ボタン（足す/かけるを統合した単一ボタン）
    const orbButton = this.createCardButton(
      "addToOrb",
      "オーブ",
      "palette-card--orb"
    );
    this.buttonsWrap.appendChild(orbButton);

    // callボタン（初期非表示、ステージに応じて表示切替）
    const callDef = CARD_DEFINITIONS["call"];
    this.callButton = this.createCardButton("call", callDef.label);
    this.callButton.style.display = "none";
    this.buttonsWrap.appendChild(this.callButton);

    // actionボタン（初期非表示、ステージに応じて表示切替）
    const actionDef = CARD_DEFINITIONS["action"];
    this.actionButton = this.createCardButton("action", actionDef.label);
    this.actionButton.style.display = "none";
    this.buttonsWrap.appendChild(this.actionButton);
  }

  /**
   * カードボタンを作成（アイコン + ラベルのDOM構造）
   * @param cssClass - 省略時は `palette-card--${type}` が使われる
   */
  private createCardButton(
    type: CardType,
    label: string,
    cssClass?: string
  ): HTMLButtonElement {
    const button = document.createElement("button");
    button.className = `palette-card ${cssClass ?? `palette-card--${type}`}`;
    button.setAttribute("aria-label", `呪文「${label}」を追加`);

    // アイコン
    const iconEl = document.createElement("span");
    iconEl.className = "icon";
    iconEl.setAttribute("aria-hidden", "true");
    const svg = CARD_ICONS[type];
    if (svg) iconEl.innerHTML = svg;
    button.appendChild(iconEl);

    // ラベル
    const lblEl = document.createElement("span");
    lblEl.className = "lbl";
    lblEl.textContent = label;
    button.appendChild(lblEl);

    // クリックハンドラ
    button.addEventListener("click", () => {
      if (this.onCardClickCallback) {
        this.onCardClickCallback(type);
      }
    });

    // ドラッグ&ドロップ対応
    button.draggable = true;
    button.addEventListener("dragstart", (e) => {
      if (!e.dataTransfer) return;
      e.dataTransfer.setData(PALETTE_MIME_TYPE, type);
      e.dataTransfer.effectAllowed = "copy";
    });

    return button;
  }
}
